import { useLocation, useNavigate } from 'react-router-dom';

import qs from 'qs';

import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { RelativeSpinner } from '@components/loading-spinner';
import {
  PasswordStrengthBar,
  usePasswordValidations,
} from '@components/password-strength-bar/password-strength-bar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useCanSetPassword, useSetPassword } from 'api/users/hooks';

import { Subtitle, Title } from './common';

import { routePaths } from '../navigation';

export type SetPasswordData = {
  password1: string;
  password2: string;
};

const Form = createForm<SetPasswordData>();

export const SetPassword = ({ uuid, passwordUuid }: { uuid: string; passwordUuid: string }) => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();
  const setPasswordMutation = useSetPassword({
    onSuccess: () => {
      navigate('/setpasswordsuccess');
    },
  });
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const password1 = (formValues['password1'] as string) ?? '';
  const password2 = (formValues['password2'] as string) ?? '';

  const { isAllValid, ValidationMessages } = usePasswordValidations(password1, password2);

  const handleSave = (data: SetPasswordData) => {
    setPasswordMutation.mutate({
      uuid,
      passwordUuid,
      password1: data.password1,
      password2: data.password2,
    });
  };
  return (
    <Stack space={6}>
      <Stack>
        <Title>{t('resetPassword')}</Title>
      </Stack>
      <Form onSubmit={handleSave} store={formStore}>
        <Form.PasswordField id="password1" label={t('password')} validators={['required']} />
        <PasswordStrengthBar password={password1} />
        <Form.PasswordField id="password2" label={t('confirmPassword')} validators={['required']} />
        <ValidationMessages />
        <Form.Submit isDisabled={!isAllValid} text={t('resetPassword')} />
      </Form>
    </Stack>
  );
};

export const InvalidLink = () => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();

  return (
    <Stack space={6}>
      <Stack>
        <Title>{t('nothingHere')}</Title>
        <Subtitle>{t('linkExpired')}</Subtitle>
      </Stack>
      <Stack space={4}>
        <Button variant="primary" text={t('signIn')} onClick={() => navigate(routePaths.signin)} />
      </Stack>
    </Stack>
  );
};

export const ResetPasswordPage = () => {
  const location = useLocation();
  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
  const uuid = parsed['uuid'] as string;
  const passwordUuid = parsed['password_uuid'] as string;

  const canSetPasswordQuery = useCanSetPassword({ uuid, passwordUuid });
  const isLoading = canSetPasswordQuery.isLoading;
  const isSuccess = !isLoading && canSetPasswordQuery.isSuccess;

  return (
    <>
      {isLoading && <RelativeSpinner />}
      {isSuccess ? <SetPassword uuid={uuid} passwordUuid={passwordUuid} /> : <InvalidLink />}
    </>
  );
};

const locale = {
  en: {
    resetPassword: 'Reset password',
    password: 'Password',
    confirmPassword: 'Confirm password',
    nothingHere: 'Nothing here?',
    linkExpired: 'This link has either expired or your password has already been set',
    forgotPassword: 'Forgot password',
    signIn: 'Sign in',
  },
};
